import * as KitDocumentsActions from './kit-document.actions';

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {KitDocument, KitDocumentSearchParams} from '@accounting/_common/models/kit-document.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const kitDocumentsFeatureKey = 'kitDocuments';

export function sortKitDocument(a: KitDocument, b: KitDocument) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<KitDocument> = createEntityAdapter<KitDocument>({
    sortComparer: sortKitDocument
});

export type KitDocumentsState = EntityStateModel<KitDocument, KitDocumentSearchParams>;

export const initialState: KitDocumentsState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const kitDocumentsReducer = createReducer(
    initialState,

    on(KitDocumentsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(KitDocumentsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(KitDocumentsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(KitDocumentsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(KitDocumentsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(KitDocumentsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(KitDocumentsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(KitDocumentsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(KitDocumentsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(KitDocumentsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(KitDocumentsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(KitDocumentsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(KitDocumentsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: KitDocumentSearchParams | undefined, item: KitDocument): boolean => {
    if (!searchParams) {
        return true;
    }

    if ((searchParams.operative || searchParams.operative === false) && item.operative !== searchParams.operative) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(item.date))) {
        return false;
    }

    if (searchParams.to && dayjs(item.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    return !(!item.comment && searchParams.query || searchParams.query && (item.comment as string).toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1);
};
